/**
 * Where an open lookup list goes so that it can be seen — on a phone with the
 * on-screen keyboard up, and on a desk — kept apart from the component so it
 * can be unit-tested (and so Fast Refresh keeps working on LookupField.tsx).
 *
 * The page scrolls the box up just far enough for its list to end above what
 * covers the bottom of the screen: the keyboard, or a bar stuck over the
 * bottom of the page (the log's footer with Save). It never scrolls so far
 * that the box slides under what is pinned over the top of the page: the
 * app's header, or a bar that sticks there (the crew's "Apply to selected
 * people" bar while anyone is ticked). Where the page cannot scroll that far
 * (the box sits near the end of the log, or rides in such a bar), the list
 * fits the room there is instead. It is shortened to that room, or, with less
 * than about two and a half rows under the box and more room above, it opens
 * above the box. A list drawn in the page's flow (`inline`, or into a
 * `listHost`) stays under its box and is only shortened.
 *
 * Review of 2026-10-08: near the end of a section the list opened under the
 * keyboard because the scroll stopped at the end of <main>; with people
 * ticked, the scroll put the box and its best matches under the bar. Review 2
 * of 2026-10-08: on a desk nothing was fitted at all — a list near the bottom
 * opened under the log's footer or past the bottom of <main> — and a phone's
 * page that shrinks for the keyboard ran crew lists behind the footer.
 */

export interface RevealGeometry {
  /** The box, now (px from the top of the layout viewport). */
  inputTop: number;
  inputBottom: number;
  /** Where the list starts when it is drawn under the box. */
  listTop: number;
  /** The list's height now: its rows, up to its usual cap. */
  listHeight: number;
  /** Its usual cap (max-h-60). */
  listMax: number;
  /** The band a person can see: under what is pinned over the top, above the keyboard and any bar stuck over the bottom. */
  visibleTop: number;
  visibleBottom: number;
  /** How far a scroll can move the box up (the page scrolled on), and back down. */
  travelOn: number;
  travelBack: number;
  /** A list drawn over the page may open above the box; one drawn in the page's flow cannot. */
  canFlip: boolean;
  /** Space kept clear at the band's edges. */
  margin: number;
  /** Between the box and a list drawn over the page (mt-1 / mb-1). */
  gap: number;
  /** The least list worth keeping under the box (about two and a half rows) before trying above it. */
  minList: number;
}

export interface RevealPlan {
  /** Scroll the page on by this much (negative: back). */
  scroll: number;
  /** Draw the list above the box. */
  above: boolean;
  /** Shorten the list to this height in px; null when its usual cap fits. */
  maxHeight: number | null;
}

export function planReveal(g: RevealGeometry): RevealPlan {
  const top = g.visibleTop + g.margin;
  const bottom = g.visibleBottom - g.margin;
  // A scroll the page can make that keeps the box at or below the band's top.
  // A box half under what is pinned over the top comes back down out from
  // under it; one scrolled wholly out of sight is left there (the page is not
  // pulled back under the finger of someone scrolling).
  const least = g.inputBottom > g.visibleTop ? -g.travelBack : 0;
  const scrollFor = (wanted: number) => Math.max(least, Math.min(wanted, g.inputTop - top, g.travelOn));
  // Held to the room there is, so a list that grows again as the search is
  // erased still ends inside the band.
  const cap = (room: number) => (room >= g.listMax || room <= 0 ? null : Math.floor(room));

  // Under the box: lift it until the list ends above the keyboard.
  const lift = scrollFor(Math.max(0, g.listTop + g.listHeight - bottom));
  const below = bottom - (g.listTop - lift);
  if (below >= Math.min(g.listHeight, g.minList) || !g.canFlip) {
    return { scroll: lift, above: false, maxHeight: cap(below) };
  }
  // Too little room under it even so: above the box, when there is more room
  // there. The box then moves only as far as it must to be seen at all.
  const liftBox = scrollFor(Math.max(0, g.inputBottom - bottom));
  const aboveRoom = g.inputTop - liftBox - g.gap - top;
  if (aboveRoom > below) return { scroll: liftBox, above: true, maxHeight: cap(aboveRoom) };
  return { scroll: lift, above: false, maxHeight: cap(below) };
}

/** Space kept clear between the list and the edges of what can be seen. */
const MARGIN = 8;
/** The list's mt-1 / mb-1. */
const LIST_GAP = 4;
/** Two and a half 44px rows, with the list's padding and border. */
const MIN_LIST = 120;

/** The list's usual cap, max-h-60 (15rem), in px. */
function listMaxPx(): number {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return 15 * (rem > 0 ? rem : 16);
}

/** The nearest ancestor that scrolls vertically (the app's <main> on a phone), or null for the page. */
export function scrollParentOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * The bottom of the bars that stick over the top of the scroller ahead of the
 * box (the crew's "Apply to selected people" bar while anyone is ticked).
 * Scrolled up, the box would slide under them. A bar sticks inside its own
 * parent, and only one that comes before the box in the page can sit over
 * it, so the bars looked at are the earlier siblings of the box and of its
 * ancestors, up to the scroller. One not stuck yet is higher up the page than
 * the box, so it reaches its place first: `top` px under `viewTop`, the
 * scroller's inner top edge inside its padding (where a bar with `top: 0`
 * sticks).
 */
export function pinnedBarsBottom(input: HTMLElement, scroller: HTMLElement | null, viewTop: number): number {
  const box = input.getBoundingClientRect();
  let bottom = -Infinity;
  for (let el: Element | null = input; el && el !== scroller; el = el.parentElement) {
    for (let s = el.previousElementSibling; s; s = s.previousElementSibling) {
      const cs = getComputedStyle(s);
      if (cs.position !== 'sticky') continue;
      const stickAt = parseFloat(cs.top);
      if (Number.isNaN(stickAt)) continue; // sticks to the bottom, if anywhere
      const r = s.getBoundingClientRect();
      if (r.height <= 0 || r.right <= box.left || r.left >= box.right) continue;
      bottom = Math.max(bottom, Math.min(r.top, viewTop + stickAt) + r.height);
    }
  }
  return bottom;
}

/**
 * Where a bar that sticks would sit in the page were it not stuck: its place
 * in the flow. Read with its stickiness off for a moment and put back before
 * anything is drawn (a sticky bar keeps its room in the flow either way, so
 * nothing else moves).
 */
function placeInFlowTop(bar: HTMLElement): number {
  const hadStyle = bar.hasAttribute('style');
  const value = bar.style.getPropertyValue('position');
  const priority = bar.style.getPropertyPriority('position');
  bar.style.setProperty('position', 'static', 'important');
  const top = bar.getBoundingClientRect().top;
  if (value) bar.style.setProperty('position', value, priority);
  else bar.style.removeProperty('position');
  if (!hadStyle && bar.getAttribute('style') === '') bar.removeAttribute('style');
  return top;
}

/**
 * The bars stuck over the bottom of the scroller after the box (the log's
 * footer with its Save: FormRenderer's `sticky bottom-0`). The list under the
 * box must end above them — they are drawn over it, on a desk as on a phone.
 * Mirrors pinnedBarsBottom: a bar sticks inside its own parent, and only one
 * that comes after the box in the page can sit over its list, so the bars
 * looked at are the later siblings of the box and of its ancestors, up to the
 * scroller.
 *
 *  - `top`: the highest top among them now. One still below the view (its
 *    parent further down) counts where it will stick: `bottom` px above
 *    `viewBottom`, the scroller's inner bottom edge less its padding (where a
 *    bar with `bottom: 0` sticks).
 *  - `flowTop`: the highest of their places in the flow. A stuck bar holds
 *    still while the page scrolls on only until its place comes up to it;
 *    from there it rises with the box, so a scroll gains no more room. A bar
 *    not stuck (the end of its parent is in view) is at its place already.
 *
 * Infinity for both: no such bar.
 */
export function pinnedBottomBarsTop(input: HTMLElement, scroller: HTMLElement | null, viewBottom: number): { top: number; flowTop: number } {
  const box = input.getBoundingClientRect();
  let top = Infinity;
  let flowTop = Infinity;
  for (let el: Element | null = input; el && el !== scroller; el = el.parentElement) {
    for (let s = el.nextElementSibling; s; s = s.nextElementSibling) {
      const cs = getComputedStyle(s);
      if (cs.position !== 'sticky') continue;
      const stickAt = parseFloat(cs.bottom);
      if (Number.isNaN(stickAt)) continue; // sticks to the top, if anywhere
      const r = s.getBoundingClientRect();
      if (r.height <= 0 || r.right <= box.left || r.left >= box.right) continue;
      top = Math.min(top, r.top, viewBottom - stickAt - r.height);
      flowTop = Math.min(flowTop, s instanceof HTMLElement ? placeInFlowTop(s) : r.top);
    }
  }
  return { top, flowTop };
}

/**
 * How far a scroll can move the box: up (the page scrolled on, to the end of
 * its range) and back down. A box riding in a bar that sticks moves only
 * until the bar sticks, and not at all once it has.
 */
export function scrollTravel(input: HTMLElement, scroller: HTMLElement | null, viewTop: number): { on: number; back: number } {
  const page = scroller ?? document.scrollingElement;
  if (!page) return { on: 0, back: 0 };
  let on = page.scrollHeight - page.clientHeight - page.scrollTop;
  let back = page.scrollTop;
  for (let a = input.parentElement; a && a !== scroller; a = a.parentElement) {
    const cs = getComputedStyle(a);
    if (cs.position === 'fixed') return { on: 0, back: 0 };
    if (cs.position === 'sticky') {
      const stickAt = parseFloat(cs.top);
      if (!Number.isNaN(stickAt)) on = Math.min(on, a.getBoundingClientRect().top - (viewTop + stickAt));
      back = 0;
    }
  }
  return { on: Math.max(0, on), back: Math.max(0, back) };
}

/**
 * Everything planReveal needs, measured from the page as it is now.
 *
 * `inFlow`: the list is drawn in the page's flow (`inline`, or into a
 * `listHost`) — under its box, and under any bar stuck over the bottom.
 * `overBars`: a list drawn over the page (z-30) is drawn over such a bar (the
 * log's footer is z-10) too, so the bar does not bound it. A phone lets it:
 * room there is scarce (a page that shrinks for the keyboard leaves ~85px
 * between the box and the footer). A desk keeps every list above the footer,
 * Save and Next in sight.
 */
export function measureReveal(input: HTMLElement, list: HTMLElement, inFlow: boolean, overBars = false): RevealGeometry {
  const vv = window.visualViewport;
  const scroller = scrollParentOf(input);
  const view = scroller?.getBoundingClientRect();
  // The scroller's inner top edge: what scrolls shows from here down.
  const viewTop = scroller && view ? view.top + scroller.clientTop : 0;
  // Where a bar stuck to the scroller sits (plus its own `top` / `bottom`): inside the scroller's padding —
  // a desk's <main> is p-6, and its log footer sticks 24px above <main>'s bottom edge (review 2, 2026-10-08).
  const padding = scroller ? getComputedStyle(scroller) : null;
  const stickTop = viewTop + (parseFloat(padding?.paddingTop ?? '') || 0);
  const stickBottom = (scroller && view ? viewTop + scroller.clientHeight : document.documentElement.clientHeight || window.innerHeight)
    - (parseFloat(padding?.paddingBottom ?? '') || 0);
  // What the screen shows at all: above the keyboard (the visual viewport), inside the scroller.
  const screenBottom = Math.min(vv ? vv.offsetTop + vv.height : window.innerHeight, view?.bottom ?? Infinity);
  const bars = overBars && !inFlow ? { top: Infinity, flowTop: Infinity } : pinnedBottomBarsTop(input, scroller, stickBottom);
  const box = input.getBoundingClientRect();
  const travel = scrollTravel(input, scroller, stickTop);
  const listMax = listMaxPx();
  return {
    inputTop: box.top,
    inputBottom: box.bottom,
    // Over the page, the list hangs right under the box (wherever it is drawn now); in the flow, where it is.
    listTop: inFlow ? list.getBoundingClientRect().top : box.bottom + LIST_GAP,
    // Its rows' full height (scrollHeight ignores a cap already set), plus its border.
    listHeight: Math.min(list.scrollHeight + (list.offsetHeight - list.clientHeight), listMax),
    listMax,
    visibleTop: Math.max(vv?.offsetTop ?? 0, viewTop, pinnedBarsBottom(input, scroller, stickTop)),
    visibleBottom: Math.min(screenBottom, bars.top),
    // Once the page brings a footer's place in the flow up to where it can be
    // seen, the footer rises with the box: scrolling further gains no room.
    travelOn: bars.flowTop === Infinity ? travel.on : Math.min(travel.on, Math.max(0, bars.flowTop - Math.min(screenBottom, bars.top))),
    travelBack: travel.back,
    canFlip: !inFlow,
    margin: MARGIN,
    gap: LIST_GAP,
    minList: MIN_LIST,
  };
}
